import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { Grid, ResponsiveContext, Spinner } from 'grommet';
import { Table } from 'grommet-icons';
import { ReportFilterDefinition, getReportDefinition } from './report-definitions';
import { ApiError, CancelablePromise, ReportService, TGetReportFiltersResponse, TReportFilterPayload } from '/src/api';
import {
  Box,
  BreadcrumbNav,
  Button,
  Card,
  CardBody,
  CardHeader,
  ClientsSelect,
  ListPage,
  ProgramSelect,
  SaveButton,
  Select,
  Skeleton,
  Text,
} from '/src/components';
import { useClientStore, useGlobalStore, useUserStore } from '/src/context';
import { TClientId, TProgramId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { SelectOption } from '/src/lib/types';
import PageNotFound from '/src/pages/404';
import { getIsMobile, refreshSessionExpiration } from '/src/utils';

const buildInitialValues = (filters: ReportFilterDefinition[]) =>
  filters.reduce<Record<string, (string | number)[] | string>>((values, filter) => {
    values[filter.key] = filter.type === 'enum' ? (filter.defaultValue ?? 'all') : [];
    return values;
  }, {});

const buildFilterPayload = (
  filters: ReportFilterDefinition[],
  values: Record<string, (string | number)[] | string>,
  options: TGetReportFiltersResponse | null
) => {
  const payload: TReportFilterPayload = {};
  filters.forEach((filter) => {
    const value = values[filter.key];
    if (Array.isArray(value)) {
      const available = options?.[filter.key] ?? [];
      const isAllSelected = available.length > 0 && available.every((option) => value.includes(option.value));
      if (value.length > 0 && !isAllSelected) payload[filter.key] = value;
    } else if (value !== 'all') {
      payload[filter.key] = value;
    }
  });
  return payload;
};

const pruneValues = (
  filters: ReportFilterDefinition[],
  values: Record<string, (string | number)[] | string>,
  options: TGetReportFiltersResponse
) => {
  let changed = false;
  const next = { ...values };
  filters.forEach((filter) => {
    const selected = values[filter.key];
    if (!Array.isArray(selected) || selected.length === 0) return;
    const available = new Set((options[filter.key] ?? []).map((option) => option.value));
    const kept = selected.filter((value) => available.has(value));
    if (kept.length !== selected.length) {
      next[filter.key] = kept;
      changed = true;
    }
  });
  return { next, changed };
};

const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const ReportGeneratorPage: React.FC = () => {
  /* Context */
  const { report_slug } = useParams();
  const globalStore = useGlobalStore();
  const clientStore = useClientStore();
  const userStore = useUserStore();
  const { t } = useTranslation();
  const screenSize = useContext(ResponsiveContext);
  const isMobile = getIsMobile(screenSize);

  const definition = getReportDefinition(report_slug);

  /* State */
  const [filterOptions, setFilterOptions] = useState<TGetReportFiltersResponse | null>(null);
  const [values, setValues] = useState<Record<string, (string | number)[] | string>>({});
  const [isFetchingOptions, setIsFetchingOptions] = useState(false);
  const [hasStaleOptions, setHasStaleOptions] = useState(false);
  const [fetchSourceKey, setFetchSourceKey] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const runRef = useRef(0);
  const fetchRef = useRef(0);
  const exportRef = useRef<CancelablePromise<Blob> | null>(null);

  /** Effects **/
  useEffect(() => {
    if (!definition) return;

    setIsGenerating(false);
    const initialValues = buildInitialValues(definition.filters);
    const clientsFilter = definition.filters.find((filter) => filter.component === 'clients');
    if (clientsFilter && userStore.isClientAdmin && clientStore.activeClientId) {
      initialValues[clientsFilter.key] = [clientStore.activeClientId];
    }
    setValues(initialValues);
    setFilterOptions(null);
    refreshFilterOptions(initialValues, null, null);

    return () => {
      runRef.current += 1;
      fetchRef.current += 1;
      exportRef.current?.cancel();
      exportRef.current = null;
    };
  }, [definition?.key]);

  if (!definition) return <PageNotFound />;

  /* Methods */
  const refreshFilterOptions = async (
    nextValues: Record<string, (string | number)[] | string>,
    sourceKey: string | null,
    currentOptions: TGetReportFiltersResponse | null
  ) => {
    fetchRef.current += 1;
    const fetchId = fetchRef.current;
    let currentValues = nextValues;
    let latestOptions = currentOptions;
    setIsFetchingOptions(true);
    setFetchSourceKey(sourceKey);

    try {
      for (;;) {
        const options = await ReportService.getFilterOptions(
          definition.key,
          buildFilterPayload(definition.filters, currentValues, latestOptions)
        );
        if (fetchRef.current !== fetchId) return;

        setFilterOptions(options);
        setHasStaleOptions(false);
        latestOptions = options;

        const { next, changed } = pruneValues(definition.filters, currentValues, options);
        if (!changed) return;

        currentValues = next;
        setValues(next);
      }
    } catch (error) {
      if (fetchRef.current !== fetchId) return;
      globalStore.handleApiError(error as ApiError, toastMessages.listReportFilters.error);
      setFilterOptions((current) => current ?? {});
      setHasStaleOptions(true);
    } finally {
      if (fetchRef.current === fetchId) setIsFetchingOptions(false);
    }
  };

  const setFilterValue = (key: string, value: (string | number)[] | string) => {
    const next = { ...values, [key]: value };
    setValues(next);
    refreshFilterOptions(next, key, filterOptions);
  };

  const handleGenerate = async () => {
    const payload = buildFilterPayload(definition.filters, values, filterOptions);

    refreshSessionExpiration();
    setIsGenerating(true);
    runRef.current += 1;
    const runId = runRef.current;

    try {
      const request = ReportService.exportReport(definition.key, payload);
      exportRef.current = request;
      const blob = await request;
      if (runRef.current !== runId) return;

      saveBlob(blob, `${definition.key}-report-${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`);
      toast.success(toastMessages.generateReport.success);
    } catch (error) {
      if (runRef.current !== runId) return;
      globalStore.handleApiError(error as ApiError, toastMessages.generateReport.error);
    } finally {
      if (runRef.current === runId) {
        exportRef.current = null;
        setIsGenerating(false);
      }
    }
  };

  const handleCancelGenerate = () => {
    runRef.current += 1;
    exportRef.current?.cancel();
    exportRef.current = null;
    setIsGenerating(false);
  };

  /* Render */
  const renderFilter = (filter: ReportFilterDefinition) => {
    const label = t(filter.labelKey as never);

    if (filter.type === 'enum') {
      return (
        <Select
          id={filter.key}
          label={label}
          options={(filter.enumOptions ?? []).map((option) => ({
            value: option.value,
            label: t(option.labelKey as never),
          }))}
          value={values[filter.key] as string}
          setValue={(value: string) => setFilterValue(filter.key, value)}
          hideOptionalText
        />
      );
    }

    if (filterOptions === null) {
      return <Skeleton height="3.75rem" />;
    }

    const options = filterOptions[filter.key] ?? [];
    const isLoadingSiblingOptions = isFetchingOptions && filter.key !== fetchSourceKey;
    const isDisabled = !isLoadingSiblingOptions && !hasStaleOptions && options.length === 0;

    if (filter.component === 'clients') {
      return (
        <ClientsSelect
          id={filter.key}
          multiple
          label={label}
          options={options as { label: string; value: number }[]}
          selectedClientIds={values[filter.key] as TClientId[]}
          setSelectedClientIds={(ids) => setFilterValue(filter.key, ids)}
          isLoadingOptions={isLoadingSiblingOptions}
          disabled={isDisabled}
          hideOptionalText
        />
      );
    }

    if (filter.component === 'programs') {
      return (
        <ProgramSelect
          id={filter.key}
          multiple
          label={label}
          options={options as SelectOption<TProgramId>[]}
          selectedProgramIds={values[filter.key] as TProgramId[]}
          setSelectedProgramIds={(ids) => setFilterValue(filter.key, ids)}
          isLoadingOptions={isLoadingSiblingOptions}
          disabled={isDisabled}
          hideOptionalText
        />
      );
    }

    return (
      <Select
        id={filter.key}
        multiple
        label={label}
        options={options}
        values={values[filter.key] as string[]}
        setValues={(nextValues: (string | number)[]) => setFilterValue(filter.key, nextValues)}
        isLoadingOptions={isLoadingSiblingOptions}
        disabled={isDisabled}
        hideOptionalText
      />
    );
  };

  const Icon = definition.icon;
  const visibleColumns = definition.columns.filter(
    (column) => userStore.isFuseAdmin || !definition.adminOnlyColumns?.includes(column)
  );
  const columnsPerList = Math.ceil(visibleColumns.length / 2);

  return (
    <ListPage
      title={t(definition.titleKey as never)}
      breadcrumbNav={
        <BreadcrumbNav
          previousPages={[{ name: t('navigation:reportsLibrary'), link: '/reports' }]}
          currentPageName={t(definition.titleKey as never)}
        />
      }
    >
      <Card flex={{ grow: 1, shrink: 1 }} alignSelf={isMobile ? undefined : 'start'}>
        <CardHeader title={`${t(definition.titleKey as never)} ${t('common:filters')}`} icon={<Icon size="24px" />} />
        <CardBody pad="1.5rem" gap="medium">
          <Text size="small" color="dark-2">
            {t(definition.descriptionKey as never)} {t('reports:noFilterHint')}
          </Text>
          <Grid columns={isMobile ? ['1fr'] : ['1fr', '1fr', '1fr']} gap="medium">
            {definition.filters.map((filter) => (
              <Box key={filter.key}>{renderFilter(filter)}</Box>
            ))}
          </Grid>
          <Box direction="row" align="center" gap="small">
            <Box width="16rem">
              <SaveButton
                label={t('reports:generateReport')}
                loadingLabel={t('reports:generatingReport')}
                isLoading={isGenerating}
                disabled={isGenerating || isFetchingOptions || hasStaleOptions || filterOptions === null}
                onClick={handleGenerate}
              />
            </Box>
            {isGenerating && (
              <Box direction="row" align="center" gap="small">
                <Button label={t('common:cancel')} onClick={handleCancelGenerate} />
                <Spinner size="small" />
                <Text size="small" color="dark-2">
                  {t('reports:generatingHint')}
                </Text>
              </Box>
            )}
          </Box>
        </CardBody>
      </Card>
      <Card width={isMobile ? undefined : '27rem'} flex={false} alignSelf={isMobile ? undefined : 'start'}>
        <CardHeader title={t('reports:includedColumns')} icon={<Table size="24px" color="brand" />}>
          <Box direction="row" align="center" gap="xsmall" flex="grow">
            <Text size="small" color="dark-1" toUpperCase>
              {t('reports:columnCountLabel')}
            </Text>
            <Box background="brand" borderRadius="999px" pad={{ horizontal: 'small', vertical: 'xxsmall' }}>
              <Text size="small" color="white" weight={500}>
                {visibleColumns.length}
              </Text>
            </Box>
          </Box>
        </CardHeader>
        <CardBody pad="1.5rem">
          <Box direction="row" gap="small">
            {[visibleColumns.slice(0, columnsPerList), visibleColumns.slice(columnsPerList)].map((list, listIndex) => (
              <Box key={listIndex} basis="1/2" gap="small">
                {list.map((column, index) => (
                  <Box key={column} direction="row" gap="xsmall">
                    <Box width="1.5rem" flex={false}>
                      <Text size="medium" color="accent-1" weight={500}>
                        {listIndex * columnsPerList + index + 1}
                      </Text>
                    </Box>
                    <Text size="medium" color="dark-1">
                      {column}
                    </Text>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </CardBody>
      </Card>
    </ListPage>
  );
};
